import React from "react";
import { DispatchProp } from "react-redux";
import { isEmpty } from "../../utils/lodash";
import { QuestionModel } from "../../models/QuestionModel";
import { BaseControl, ControlProps } from "./BaseControl";
import { ChoiceModel } from "../../models/ChoiceModel";
import { QuestionType } from "../../models/enums/QuestionType";
import { ElementWithSettings } from "../../models/ElementWithSettings";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { getChoice } from "../../models/utilities/variableUtilities";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { blurFocusedField } from "../../utils/index";
import { VariableModel } from "../../models/VariableModel";
import { translate } from "../../models/utilities/translationUtilities";
import { getRendererProps } from "../renderers/BaseRenderer";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import ImageAreaSelectionRenderer from "../renderers/v2/ImageAreaSelectionRenderer";
import ImageAreaRenderer from "../renderers/v2/ImageAreaRenderer";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";

interface Props extends ControlProps, DispatchProp {}

interface State {
    filteredAnswers: ChoiceModel[];
}

export class ImageAreaControl extends BaseControl<Props, State> {
    onSelectArea(variable: VariableModel) {
        const { choiceList } = variable;
        let choice = this.getAreaCurrentChoice(this.props, variable);
        const nextIndex =
            choice === undefined ? 0 : choiceList.findIndex((c) => c.value === choice.value) + 1;

        if (nextIndex >= choiceList.length) {
            this.setMentions(this.props, variable.name, (mentions) => mentions.clear());
            return;
        }
        choice = choiceList[nextIndex];
        this.setMentions(this.props, variable.name, (mentions) => mentions.set(choice.value));
    }

    onSelectChoice(choice: ChoiceModel) {
        blurFocusedField();
        const { question } = this.props;
        const [variable] = question.variables;
        const mentions = this.getMentions(this.props);
        if (
            hasSetting(question, "MaxAllowedAnswers") &&
            mentions.length >= getSettingNumber(question, "MaxAllowedAnswers") &&
            !mentions.find((m) => m.value === choice.value)
        ) {
            const [firstMention] = mentions;
            this.setMentions(this.props, variable.name, (m) => m.toggle(firstMention.value));
        }

        this.setMentions(this.props, variable.name, (m) => {
            const isSelectedExclusive = getSettingBoolean(choice, "Exclusive");
            if (variable.choiceList !== undefined) {
                variable.choiceList.forEach((c) => {
                    if (isSelectedExclusive || getSettingBoolean(c, "Exclusive")) {
                        m = m.removeValue(c.value);
                    }
                });
            }
            return m.toggle(choice.value);
        });
    }

    getAreaCurrentChoice(props: Props, area: VariableModel) {
        const value = this.getMentionValue(props, area.name);
        return getChoice(area, value);
    }

    isChoiceSelected(choice: ChoiceModel) {
        return this.getMentions(this.props).some((m) => m.value === choice.value);
    }

    getDimension(element: ElementWithSettings, name: string) {
        const dimension = getSettingNumber(element, name) * 100;
        return Number.isNaN(dimension) ? "0" : `${dimension}%`;
    }

    renderMapChoice(question: QuestionModel, choice: ChoiceModel) {
        return (
            <ImageAreaSelectionRenderer
                {...getRendererProps(this.props)}
                key={choice.index}
                selected={this.isChoiceSelected(choice)}
                top={this.getDimension(choice, "Top")}
                width={this.getDimension(choice, "Width")}
                height={this.getDimension(choice, "Height")}
                left={this.getDimension(choice, "Left")}
                color={getSettingString(choice, "Color")}
                onSelect={() => this.onSelectChoice(choice)}
                text={translate(choice, "TEXT", this.props.language)}
                variableName={choice.elementId}
            />
        );
    }

    renderMapArea(question: QuestionModel, variable: VariableModel) {
        const choice = this.getAreaCurrentChoice(this.props, variable);

        return (
            <ImageAreaSelectionRenderer
                {...getRendererProps(this.props)}
                key={variable.index}
                selected={choice !== undefined}
                top={this.getDimension(variable, "Top")}
                width={this.getDimension(variable, "Width")}
                height={this.getDimension(variable, "Height")}
                left={this.getDimension(variable, "Left")}
                color={choice !== undefined ? getSettingString(choice, "Color") : null}
                onSelect={() => this.onSelectArea(variable)}
                text={translate(variable, "TEXT", this.props.language)}
                variableName={variable.elementId}
                areaCode={choice !== undefined ? choice.value : "none"}
            />
        );
    }

    renderMap(question: QuestionModel) {
        const variables = this.availableAreas(this.props);
        if (question.type === QuestionType.ImageAreaEvaluator) {
            return variables.map((variable) => this.renderMapArea(question, variable));
        }
        const [firstVariable] = variables;
        const choiceList = firstVariable ? firstVariable.choiceList : [];
        return choiceList.map((answer) => this.renderMapChoice(question, answer));
    }

    availableAreas = (props: Props) => {
        const { question, sequencedVariables } = props;
        if (question.type === QuestionType.ImageAreaEvaluator) {
            return sequencedVariables.filter((v) => !isEmpty(v.settings));
        }
        return sequencedVariables.filter((v) => v.choiceList && v.choiceList.length > 0);
    };

    renderQuestion(question: QuestionModel) {
        const { language } = this.props;

        return (
            <ImageAreaRenderer
                {...getRendererProps(this.props)}
                choices={this.props.sequencedVariables[0].choiceList}
                imageSrc={translate(question, "ImageUrl", language)}
                legendLabel={translate(question, "LegendLabel", language, "Legend")}
                height={getSettingString(question, "Height")}
                width={getSettingString(question, "Width")}
            >
                {this.renderMap(question)}
            </ImageAreaRenderer>
        );
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { questionnaire, question, showSoftPrompt, useClientValidation } = this.props;
        const bypassClientValidation = isQuestionValidatedServerSide(
            question.type,
            useClientValidation,
        );

        const availableAreas = this.availableAreas(this.props);

        const choices =
            question.type === QuestionType.ImageAreaSelector
                ? availableAreas[0].choiceList.filter((answer) => this.isChoiceSelected(answer))
                : availableAreas
                      .map((area) => this.getAreaCurrentChoice(this.props, area))
                      .filter((answer) => answer !== undefined);

        if (this.isSoftPrompt(this.props) && !this.hasMentions(this.props)) {
            return this.validateSoftPrompt(this.props);
        }

        if (bypassClientValidation) {
            return new ValidationBuilder().build();
        }

        if (choices.length > 0) {
            const min = this.minValue();
            const max = this.maxValue();
            if (min !== null && max !== null) {
                if (choices.length < min || choices.length > max) {
                    return this.failValidation(availableAreas, "OutOfBounds", {
                        min: String(min),
                        max: String(max),
                    });
                }
            } else {
                if (min !== null && choices.length < min) {
                    return this.failValidation(availableAreas, "InvalidChoicesMin", {
                        min: String(min),
                    });
                }
                if (max !== null && choices.length > max) {
                    return this.failValidation(availableAreas, "InvalidChoicesMax", {
                        max: String(max),
                    });
                }
            }
        } else if (!showSoftPrompt && this.isAnswerRequired(questionnaire, question)) {
            return this.failValidation(availableAreas, "IncompleteAnswer");
        }

        const validationResult = new ValidationBuilder().build();
        return validationResult;
    }
}
